import {useMutation, useQuery} from '@tanstack/react-query';
import {snackbarAtom} from 'atoms/snackbar';
import {AiEditorService} from 'client';
import {useSetAtom} from 'jotai';
import {useEffect, useState} from 'react';
import {getService} from 'utilities';

export const useAIGenerate = () => {
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const [generateTaskId, setGenerateTaskId] = useState<string | undefined>(undefined);
  const [generateData, setGenerateData] = useState<string | undefined>(undefined);

  const {mutateAsync: generateApi} = useMutation({
    mutationFn: getService(AiEditorService).generateApiV1EditorGeneratePost,
  });

  const isGenerating = !generateData && !!generateTaskId;

  const {data: importStatus} = useQuery({
    queryKey: ['generate', 'status', generateTaskId],
    enabled: isGenerating,
    refetchInterval: 1000,
    queryFn: async () => {
      const response = await getService(
        AiEditorService
      ).getGenerationStatusApiV1EditorGenerateTaskIdGet(generateTaskId as string);
      return response as {status: string; detail?: string; result: string};
    },
  });

  useEffect(() => {
    if (importStatus?.detail) {
      setSnackbarMessage({text: 'Error: ' + importStatus?.detail});
      setGenerateTaskId(undefined);
      return;
    }

    if (importStatus?.status === 'completed') {
      setGenerateTaskId(undefined);
      setGenerateData(importStatus.result);
    }
  }, [importStatus, setSnackbarMessage]);

  const generate = async (prompt: string, data: string) => {
    const response = await generateApi({prompt, data});
    setGenerateTaskId(response.task_id);
  };

  const clearData = () => {
    setGenerateData(undefined);
  };

  return {
    generate,
    generateData,
    isGenerating,
    clearData,
  };
};

export const useAIQuick = () => {
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const [quickData, setQuickData] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const {mutateAsync: quickApi} = useMutation({
    mutationFn: getService(AiEditorService).quickApiV1EditorQuickPost,
  });

  const generate = async (prompt: string, data: string) => {
    setIsLoading(true);
    try {
      const response = await quickApi({prompt, data});
      setQuickData(
        typeof response === 'string' ? response : (response as {result?: string}).result
      );
    } catch (error) {
      setSnackbarMessage({text: 'Error: ' + (error as Error).message});
    } finally {
      setIsLoading(false);
    }
  };

  const clearData = () => {
    setQuickData(undefined);
  };

  return {
    generate,
    generateData: quickData,
    isGenerating: isLoading,
    clearData,
  };
};
